import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../auth/AuthContext";
import AppLayout from "../components/AppLayout";
import { Empty, ErrorNote, Loading } from "../components/Feedback";
import { Badge, Btn, Card, Field, Input, Textarea } from "../components/Shared";
import { useApi } from "../useApi";

// "New project" for an account with the curator permission (or an admin): its creator becomes
// the project's first curator and adds the other members on the project page.
function NewProjectForm({ onCancel }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({ slug: "", name: "", description: "" });
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async e => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const p = await api.post("/projects", form);
      navigate(`/projects/${p.id}`);
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  return (
    <Card style={{ marginBottom: 24 }}>
      <form onSubmit={submit} aria-label="New project" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>New project</h2>
        <ErrorNote error={error} />
        <Field label="Name" required><Input value={form.name} onChange={set("name")} /></Field>
        <Field label="Short name (slug)" required hint="Lowercase letters, digits and _; the built database is proj_<slug>.">
          <Input value={form.slug} onChange={set("slug")} pattern="[a-z][a-z0-9_]{0,39}" />
        </Field>
        <Field label="Description"><Textarea value={form.description} onChange={set("description")} /></Field>
        <p style={{ margin: 0, fontSize: 13, color: "#4b5563" }}>
          You become the project's curator; you add its members on the project page.
        </p>
        <div style={{ display: "flex", gap: 10 }}>
          <Btn type="submit" disabled={busy || !form.name || !form.slug}>Create project</Btn>
          <Btn variant="secondary" onClick={onCancel}>Cancel</Btn>
        </div>
      </form>
    </Card>
  );
}

// The project list's filter (N6, 2026-10-14): the project field TEMPLATES of a choice type that a
// visible project holds a copy of (task F: grouped by template, so a renamed copy still filters;
// GET /api/fields/project-filters -> [{key, label, options}]). Each ticked option is one
// ?field=<key>&value=<option> pair of GET /api/projects (repeatable; a project must hold all).
// The choice lives in the page's URL, so a filtered list can be bookmarked and shared.
export function filterPairs(params) {
  const fields = params.getAll("field");
  const values = params.getAll("value");
  return fields.map((f, i) => [f, values[i]]).filter(([f, v]) => f && v != null);
}

export function projectsPath(pairs) {
  if (!pairs.length) return "/projects";
  const q = new URLSearchParams();
  for (const [f, v] of pairs) { q.append("field", f); q.append("value", v); }
  return `/projects?${q.toString()}`;
}

function ProjectFilter({ filters, pairs, onChange }) {
  const on = (key, option) => pairs.some(([f, v]) => f === key && v === option);
  const toggle = (key, option) => onChange(on(key, option)
    ? pairs.filter(([f, v]) => !(f === key && v === option))
    : [...pairs, [key, option]]);
  return (
    <Card style={{ marginBottom: 18, padding: "14px 20px" }}>
      <div role="group" aria-labelledby="project-filter-title">
        <h2 id="project-filter-title" style={{ margin: "0 0 4px", fontSize: 15, fontWeight: 700 }}>Filter projects</h2>
        <p style={{ margin: "0 0 10px", fontSize: 12, color: "#6b7280" }}>
          A project is listed when it has every option ticked.
        </p>
        <div style={{ display: "flex", gap: "12px 28px", flexWrap: "wrap" }}>
          {filters.map(f => (
            <fieldset key={f.key} style={{ border: "none", margin: 0, padding: 0, minWidth: 0 }}>
              <legend style={{ fontSize: 12, fontWeight: 600, color: "#374151", letterSpacing: "0.05em",
                               textTransform: "uppercase", marginBottom: 6, padding: 0 }}>{f.label}</legend>
              <div style={{ display: "flex", gap: "4px 14px", flexWrap: "wrap" }}>
                {(f.options || []).map(o => (
                  <label key={o} style={{ display: "inline-flex", gap: 6, alignItems: "center", fontSize: 14 }}>
                    <input type="checkbox" checked={on(f.key, o)} onChange={() => toggle(f.key, o)} />
                    {o}
                  </label>
                ))}
              </div>
            </fieldset>
          ))}
        </div>
        {pairs.length > 0 && (
          <div style={{ marginTop: 10 }}><Btn variant="secondary" onClick={() => onChange([])}>Clear filters</Btn></div>
        )}
      </div>
    </Card>
  );
}

export default function ProjectsPage() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const pairs = filterPairs(params);
  const { data, error, loading } = useApi(projectsPath(pairs));
  const filters = useApi("/fields/project-filters");
  const [creating, setCreating] = useState(false);
  const projects = Array.isArray(data) ? data : [];
  const filterList = Array.isArray(filters.data) ? filters.data.filter(f => (f.options || []).length) : [];
  const setPairs = next => {
    const q = new URLSearchParams();
    for (const [f, v] of next) { q.append("field", f); q.append("value", v); }
    setParams(q, { replace: true });
  };

  return (
    <AppLayout title="Projects">
      {(user?.is_curator || user?.is_admin) && !creating && (
        <div style={{ marginBottom: 18 }}><Btn onClick={() => setCreating(true)}>New project</Btn></div>
      )}
      {creating && <NewProjectForm onCancel={() => setCreating(false)} />}
      {/* No filter when no project template of a choice type is copied into a visible project. */}
      {filterList.length > 0 && <ProjectFilter filters={filterList} pairs={pairs} onChange={setPairs} />}
      <ErrorNote error={error} />
      {loading ? <Loading /> : projects.length === 0 && pairs.length > 0 ? (
        <Empty>No project has every option ticked in the filter.</Empty>
      ) : projects.length === 0 ? (
        user && !user.is_admin && (user.memberships || []).length === 0 ? (
          // A new Keycloak account (self-registration) is a member of nothing: say why it is empty.
          <Empty>You are not a member of any project yet, so there is nothing to show. A project's
            curator adds you to it{user.is_curator ? "; you may also create one" : ""}.</Empty>
        ) : <Empty>No projects yet.</Empty>
      ) : (
        <div style={{ display: "grid", gap: 14 }}>
          {projects.map(p => (
            <Card key={p.id} style={{ padding: "20px 24px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                <div>
                  <h2 style={{ margin: "0 0 4px", fontSize: 17, fontWeight: 700 }}>
                    <Link to={`/projects/${p.id}`} style={{ color: "#1a1a1a" }}>{p.name}</Link>
                  </h2>
                  <div style={{ fontSize: 12, color: "#6b7280", fontFamily: "'JetBrains Mono', monospace" }}>{p.slug}</div>
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "flex-start" }}>
                  {p.my_role && <Badge color={p.my_role === "curator" ? "green" : "blue"}>{p.my_role}</Badge>}
                  {p.inactive_since && <Badge color="gray">inactive</Badge>}
                </div>
              </div>
              {p.description && <p style={{ margin: "10px 0 0", fontSize: 14, color: "#374151" }}>{p.description}</p>}
              <p style={{ margin: "10px 0 0", fontSize: 13, color: "#6b7280" }}>
                {p.study_count} stud{p.study_count === 1 ? "y" : "ies"}
              </p>
            </Card>
          ))}
        </div>
      )}
    </AppLayout>
  );
}
